import { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Award, Loader2, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDividendsTab = ({ token }) => {
  const [dividendStats, setDividendStats] = useState(null);
  const [dividendLoading, setDividendLoading] = useState(false);
  const [recentDividends, setRecentDividends] = useState([]);

  useEffect(() => { fetchDividendStats(); }, []);

  const fetchDividendStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/dividends/stats`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setDividendStats(await res.json());
      const recentRes = await fetch(`${API_URL}/api/dividends/recent?days=30`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (recentRes.ok) { const d = await recentRes.json(); setRecentDividends(d.events || []); }
    } catch (err) { console.error('Error fetching dividend stats:', err); }
  };

  const scanDividends = async () => {
    setDividendLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/dividends/scan?limit=100`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Scanned ${data.scanned} stocks, found ${data.new_events_found} new dividend events`);
        fetchDividendStats();
      } else toast.error('Failed to scan for dividends');
    } catch { toast.error('Error scanning dividends'); }
    finally { setDividendLoading(false); }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="admin-dividends-tab">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Award className="h-6 w-6 text-green-500" />
            <h3 className="text-lg font-semibold text-white">Dividend Events</h3>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-xl font-bold text-white">{dividendStats?.total_events || 0}</p>
              <p className="text-xs text-slate-400">Total Events</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-green-400">{dividendStats?.recent_events_30d || 0}</p>
              <p className="text-xs text-slate-400">Last 30 Days</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-blue-400">{dividendStats?.unique_symbols || 0}</p>
              <p className="text-xs text-slate-400">Symbols</p>
            </div>
            <Button onClick={scanDividends} disabled={dividendLoading} className="bg-green-600 hover:bg-green-500">
              {dividendLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Scan for Dividends
            </Button>
          </div>
        </div>
        <p className="text-slate-400 text-sm mb-4">
          Dividends are automatically detected and award bonus points to players holding the stock on the ex-dividend date.
        </p>
        {recentDividends.length > 0 && (
          <div className="mt-4">
            <h4 className="text-white font-medium mb-2">Recent Dividend Events</h4>
            <div className="bg-slate-800/50 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2 px-4 text-slate-400 text-sm font-medium">Symbol</th>
                    <th className="text-left py-2 px-4 text-slate-400 text-sm font-medium">Name</th>
                    <th className="text-left py-2 px-4 text-slate-400 text-sm font-medium">Ex-Dividend Date</th>
                    <th className="text-right py-2 px-4 text-slate-400 text-sm font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {recentDividends.slice(0, 10).map((div, idx) => (
                    <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-2 px-4"><span className="font-medium text-white">{div.symbol}</span></td>
                      <td className="py-2 px-4 text-slate-300">{div.instrument_name}</td>
                      <td className="py-2 px-4 text-slate-300">{new Date(div.ex_dividend_date).toLocaleDateString()}</td>
                      <td className="py-2 px-4 text-right text-green-400">${div.amount?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
